import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { selectElements, clearSelection } from '../slideEditorSlice';
import { getWidget } from '../interactives/widgetRegistry';

import type {
  ElementPosition,
  ElementPositionOverride,
  SlideElement,
  TextElement,
  ShapeElement,
  ImageElement,
  InteractiveElement,
  InteractiveWidgetType,
  ResolvedTheme,
  Slide,
  SlideContentV1,
  VideoElement,
} from '@/shared/types/slide';
import { MIN_ELEMENT_SIZE, SLIDE_PAGE_WIDTH, SLIDE_PAGE_HEIGHT } from '@/shared/types/slide';
import { resolveStoredElement } from '@/shared/theming/resolve';
import type { RootState } from '@/store';

const HARD_DEFAULT_POSITION: ElementPosition = {
  x: 80,
  y: 80,
  width: 240,
  height: 80,
  rotation: 0,
  zIndex: 1,
};

/** Fill a partial/absent stored position to a full one with the editor hard
 *  defaults (used when no slide/theme is available to resolve against). */
function completePosition(pos: ElementPositionOverride | undefined): ElementPosition {
  return {
    x: pos?.x ?? HARD_DEFAULT_POSITION.x,
    y: pos?.y ?? HARD_DEFAULT_POSITION.y,
    width: pos?.width ?? HARD_DEFAULT_POSITION.width,
    height: pos?.height ?? HARD_DEFAULT_POSITION.height,
    rotation: pos?.rotation ?? HARD_DEFAULT_POSITION.rotation,
    zIndex: pos?.zIndex ?? HARD_DEFAULT_POSITION.zIndex,
  };
}

// ---------------------------------------------------------------------------
// Hook: CRUD operations for slide elements.
//
// The hook does NOT hold presentation data in Redux.  Instead the editor page
// passes a `mutateContent` callback that applies changes to the in-memory
// content and triggers autosave via RTK Query.  The hook reads the *current
// slide's elements* from the content object provided by the page.
// ---------------------------------------------------------------------------

interface UseSlideElementsOptions {
  /** Current slide (derived from presentation content). */
  currentSlide: Slide | null | undefined;
  /** Deck theme — used to resolve placeholder geometry for mutation ops (§8.2). */
  theme: ResolvedTheme;
  /** Callback to apply a content mutation.  The editor page wires this to autosave. */
  mutateContent: (updater: (draft: SlideContentV1) => void) => void;
}

export interface SlideElementUpdate {
  id: string;
  updates: Partial<SlideElement>;
}

export function useSlideElements({ currentSlide, theme, mutateContent }: UseSlideElementsOptions) {
  const dispatch = useDispatch();
  const selectedElementIds = useSelector((s: RootState) => s.slideEditor.selectedElementIds);
  const currentSlideId = useSelector((s: RootState) => s.slideEditor.currentSlideId);

  // `elements` and `selectedElement` are the STORED (possibly sparse) elements —
  // the write base. Display/geometry use the resolved view (§8.2).
  const elements = currentSlide?.elements ?? [];
  const selectedElement = elements.find((el) => selectedElementIds.includes(el.id)) ?? null;
  const selectedElementResolved = useMemo(
    () =>
      selectedElement && currentSlide
        ? resolveStoredElement(selectedElement, currentSlide, theme)
        : null,
    [selectedElement, currentSlide, theme]
  );

  /** Concrete geometry of a stored element (placeholder inherited from the layout). */
  const geometryOf = useCallback(
    (el: SlideElement): ElementPosition =>
      currentSlide
        ? resolveStoredElement(el, currentSlide, theme).position
        : completePosition(el.position),
    [currentSlide, theme]
  );

  // -- helpers ---------------------------------------------------------------

  /** Replace the current slide's element array in the content tree. */
  const setElements = useCallback(
    (next: SlideElement[]) => {
      if (!currentSlideId) return;
      const sid = currentSlideId;
      mutateContent((draft) => {
        const slide = draft.presentation.slides.find((s) => s.id === sid);
        if (slide) slide.elements = next;
      });
    },
    [currentSlideId, mutateContent]
  );

  /** Normalise rotation to [0, 360). */
  const normalizeRotation = (deg: number) => ((deg % 360) + 360) % 360;

  /** Clamp position so the element can't fully leave the canvas on any side. */
  const clampPosition = useCallback((pos: ElementPosition): ElementPosition => {
    const w = Math.max(MIN_ELEMENT_SIZE, pos.width);
    const h = Math.max(MIN_ELEMENT_SIZE, pos.height);
    return {
      ...pos,
      width: w,
      height: h,
      x: Math.max(-w, Math.min(SLIDE_PAGE_WIDTH, pos.x)),
      y: Math.max(-h, Math.min(SLIDE_PAGE_HEIGHT, pos.y)),
      rotation: normalizeRotation(pos.rotation),
      zIndex: Math.max(1, pos.zIndex),
    };
  }, []);

  const mergeElementUpdate = useCallback(
    (el: SlideElement, updates: Partial<SlideElement>): SlideElement => {
      // Deep-merge content to avoid losing nested fields (e.g. tiptapDoc)
      const mergedContent = updates.content ? { ...el.content, ...updates.content } : el.content;
      const merged = { ...el, ...updates, content: mergedContent } as SlideElement;
      if (updates.position) {
        merged.position = { ...el.position, ...updates.position };
      }
      return merged;
    },
    []
  );

  // -- CRUD ------------------------------------------------------------------

  const addElements = useCallback(
    (newElements: SlideElement[]) => {
      if (newElements.length === 0) return;
      setElements([
        ...elements,
        ...newElements.map(
          (element) =>
            ({
              ...element,
              position: clampPosition(completePosition(element.position)),
            }) as SlideElement
        ),
      ]);
    },
    [clampPosition, elements, setElements]
  );

  const addElement = useCallback(
    (
      element:
        | Omit<TextElement, 'id'>
        | Omit<ShapeElement, 'id'>
        | Omit<ImageElement, 'id'>
        | Omit<InteractiveElement, 'id'>
        | Omit<VideoElement, 'id'>
    ): string => {
      const id = crypto.randomUUID();
      const newEl = {
        ...element,
        id,
        position: clampPosition(completePosition(element.position)),
      } as SlideElement;
      setElements([...elements, newEl]);
      return id;
    },
    [clampPosition, elements, setElements]
  );

  const updateElement = useCallback(
    (elementId: string, updates: Partial<SlideElement>) => {
      const updated = elements.map((el) =>
        el.id === elementId ? mergeElementUpdate(el, updates) : el
      );
      setElements(updated);
    },
    [elements, mergeElementUpdate, setElements]
  );

  const updateElements = useCallback(
    (updates: SlideElementUpdate[]) => {
      if (updates.length === 0) return;
      const updatesById = new Map(updates.map((update) => [update.id, update.updates]));
      const updated = elements.map((el) => {
        const patch = updatesById.get(el.id);
        return patch ? mergeElementUpdate(el, patch) : el;
      });
      setElements(updated);
    },
    [elements, mergeElementUpdate, setElements]
  );

  const removeElements = useCallback(
    (elementIds: string[]) => {
      if (elementIds.length === 0) return;
      const idsToRemove = new Set(elementIds);
      setElements(elements.filter((el) => !idsToRemove.has(el.id)));
      if (selectedElementIds.some((id) => idsToRemove.has(id))) {
        dispatch(clearSelection());
      }
    },
    [elements, setElements, selectedElementIds, dispatch]
  );

  const removeElement = useCallback(
    (elementId: string) => {
      removeElements([elementId]);
    },
    [removeElements]
  );

  const duplicateElement = useCallback(
    (elementId: string): string | undefined => {
      const source = elements.find((el) => el.id === elementId);
      if (!source) return undefined;
      const id = crypto.randomUUID();
      const basePos = geometryOf(source);
      const dup = structuredClone(source);
      dup.id = id;
      // The copy becomes a free element so it doesn't overlap the placeholder slot (§8.2).
      if (dup.type === 'text') delete dup.placeholderId;
      dup.position = clampPosition({ ...basePos, x: basePos.x + 20, y: basePos.y + 20 });
      setElements([...elements, dup]);
      return id;
    },
    [clampPosition, elements, geometryOf, setElements]
  );

  // -- z-order ---------------------------------------------------------------

  const moveToFront = useCallback(
    (elementId: string) => {
      const maxZ = Math.max(1, ...elements.map((el) => geometryOf(el).zIndex));
      // Write only zIndex — other geometry keeps inheriting from the layout (§8.2).
      updateElement(elementId, { position: { zIndex: maxZ + 1 } });
    },
    [elements, geometryOf, updateElement]
  );

  const moveToBack = useCallback(
    (elementId: string) => {
      const el = elements.find((e) => e.id === elementId);
      if (!el) return;
      // Shift all elements' zIndex up by 1, set target to 1 (zIndex-only override).
      const updated = elements.map((e) => {
        const zIndex = e.id === elementId ? 1 : geometryOf(e).zIndex + 1;
        return { ...e, position: { ...(e.position ?? {}), zIndex } };
      });
      setElements(updated);
    },
    [elements, geometryOf, setElements]
  );

  // -- convenience creators --------------------------------------------------

  const createTextElement = useCallback(
    (text = 'New text', x = 100, y = 100): string =>
      addElement({
        type: 'text',
        content: { text },
        position: { x, y, width: 240, height: 80, rotation: 0, zIndex: elements.length + 1 },
        style: {
          fontSize: 22,
          color: '#000000',
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'normal',
          lineHeight: 1.4,
          padding: 9.6,
          textAlign: 'left',
        },
      } as Omit<TextElement, 'id'>),
    [addElement, elements.length]
  );

  const createShapeElement = useCallback(
    (shapeType: ShapeElement['content']['shapeType'] = 'rectangle', x = 200, y = 200): string =>
      addElement({
        type: 'shape',
        content: {
          shapeType,
          fillColor: '#3498db',
          strokeColor: '#2980b9',
          strokeWidth: 2,
        },
        position: { x, y, width: 100, height: 100, rotation: 0, zIndex: elements.length + 1 },
      } as Omit<ShapeElement, 'id'>),
    [addElement, elements.length]
  );

  const createImageElement = useCallback(
    (
      opts: {
        src?: string;
        imageQuery?: string;
        alt?: string;
        width?: number;
        height?: number;
        storageBucket?: string;
        storagePath?: string;
      } = {},
      x?: number,
      y?: number
    ): string => {
      const w = opts.width ?? 300;
      const h = opts.height ?? 200;
      const cx = x ?? Math.round((SLIDE_PAGE_WIDTH - w) / 2);
      const cy = y ?? Math.round((SLIDE_PAGE_HEIGHT - h) / 2);
      return addElement({
        type: 'image',
        content: {
          src: opts.src,
          imageQuery: opts.imageQuery,
          alt: opts.alt,
          storageBucket: opts.storageBucket,
          storagePath: opts.storagePath,
        },
        position: { x: cx, y: cy, width: w, height: h, rotation: 0, zIndex: elements.length + 1 },
      } as Omit<ImageElement, 'id'>);
    },
    [addElement, elements.length]
  );

  const createInteractiveElement = useCallback(
    (widgetType: InteractiveWidgetType, x?: number, y?: number): string => {
      const entry = getWidget(widgetType);
      const w = entry?.defaultWidth ?? 240;
      const h = entry?.defaultHeight ?? 320;
      const cx = x ?? Math.round((SLIDE_PAGE_WIDTH - w) / 2);
      const cy = y ?? Math.round((SLIDE_PAGE_HEIGHT - h) / 2);
      return addElement({
        type: 'interactive',
        content: { widgetType, config: entry?.defaultConfig ?? {} },
        position: { x: cx, y: cy, width: w, height: h, rotation: 0, zIndex: elements.length + 1 },
      } as Omit<InteractiveElement, 'id'>);
    },
    [addElement, elements.length]
  );

  const createVideoElement = useCallback(
    (
      opts: {
        videoId: string;
        title?: string;
        channelName?: string;
        thumbnailUrl?: string;
        width?: number;
        height?: number;
      },
      x?: number,
      y?: number
    ): string => {
      // Default 16:9 box that fits a typical mid-canvas placement.
      const w = opts.width ?? 480;
      const h = opts.height ?? 270;
      const cx = x ?? Math.round((SLIDE_PAGE_WIDTH - w) / 2);
      const cy = y ?? Math.round((SLIDE_PAGE_HEIGHT - h) / 2);
      const zIndex = Math.max(0, ...elements.map((el) => geometryOf(el).zIndex)) + 1;
      return addElement({
        type: 'video',
        content: {
          source: 'youtube',
          videoId: opts.videoId,
          title: opts.title,
          channelName: opts.channelName,
          thumbnailUrl: opts.thumbnailUrl,
        },
        position: { x: cx, y: cy, width: w, height: h, rotation: 0, zIndex },
      } as Omit<VideoElement, 'id'>);
    },
    [addElement, elements, geometryOf]
  );

  // -- selection helpers -----------------------------------------------------

  const selectElement = useCallback(
    (id: string | null) => {
      if (id) dispatch(selectElements([id]));
      else dispatch(clearSelection());
    },
    [dispatch]
  );

  return {
    // data
    elements,
    currentSlideId,
    selectedElement,
    selectedElementResolved,
    selectedElementIds,
    geometryOf,

    // selection
    selectElement,
    clearSelection: () => dispatch(clearSelection()),

    // CRUD
    addElements,
    addElement,
    updateElements,
    updateElement,
    removeElements,
    removeElement,
    duplicateElement,

    // z-order
    moveToFront,
    moveToBack,

    // convenience
    createTextElement,
    createShapeElement,
    createImageElement,
    createInteractiveElement,
    createVideoElement,
  };
}
